import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';


import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCopy from '@mui/icons-material/ContentCopy';
import VpnKey from '@mui/icons-material/VpnKey';
import Refresh from '@mui/icons-material/Refresh';
import Delete from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';


import AlertDialog from '../utility/AlertDialog';
import { getSessionIdToken } from '../auth/apiAuth';

import axios from 'axios';

const maskApiKey = (value) => {
    if (!value || value.length < 8) return '••••••••••••••••••••••••';
    return value.substring(0, 4) + '••••••••••••••••••••' + value.substring(value.length - 4);
};

export default function LabelsApiKey({ auth, selectedProject, templates, loadingTemplates, onSnackbar, deactivation }) {
    const [keyStatus, setKeyStatus] = useState(null);
    const [newKeyValue, setNewKeyValue] = useState(null);
    const [showKey, setShowKey] = useState(false);
    const [loadingKey, setLoadingKey] = useState(false);

    const API_BASE = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        if (selectedProject) fetchKeyStatus();
    }, [selectedProject]);


    const fetchKeyStatus = async () => {
        setLoadingKey(true);
        try {
            const token = await getSessionIdToken();
            const { data } = await axios.get(`${API_BASE}/labels/api-key`, {
                headers: { Authorization: token },
                params: { project_id: selectedProject }
            });
            setKeyStatus(data);
        } catch (err) {
            setKeyStatus(null);
        } finally {
            setLoadingKey(false);
        }
    };



    const handleCreateKey = async () => {
        setLoadingKey(true);
        try {
            const token = await getSessionIdToken();
            const { data } = await axios.post(`${API_BASE}/labels/api-key`, {}, {
                headers: { Authorization: token, 'Content-Type': 'application/json' }
            });
            setNewKeyValue(data.api_key || null);
            setKeyStatus({ exists: true, name: `${auth.userState.email}-api` });
            onSnackbar('API key created successfully. Save it now — it won\'t be shown again.', 'warning');
        } catch (err) {
            const msg = err.response?.data?.error || 'Error creating the API key';
            onSnackbar(msg, 'error');
        } finally {
            setLoadingKey(false);
        }
    };

    const handleRegenerateKey = async () => {
        setLoadingKey(true);
        setNewKeyValue(null);
        try {
            const token = await getSessionIdToken();
            const { data } = await axios.put(`${API_BASE}/labels/api-key`, {}, {
                headers: { Authorization: token, 'Content-Type': 'application/json' }
            });
            setNewKeyValue(data.api_key || null);
            onSnackbar('API key regenerated. The previous key is immediately invalid.', 'warning');
        } catch (err) {
            onSnackbar('Error regenerating the API key', 'error');
        } finally {
            setLoadingKey(false);
        }
    };

    const handleDeleteKey = async () => {
        setLoadingKey(true);
        try {
            const token = await getSessionIdToken();
            await axios.delete(`${API_BASE}/labels/api-key`, {
                headers: { Authorization: token }
            });
            setKeyStatus({ exists: false });
            setNewKeyValue(null);
            onSnackbar('API key removed from the Labels service.', 'success');
        } catch (err) {
            onSnackbar('Error deleting the API key', 'error');
        } finally {
            setLoadingKey(false);
        }
    };

    const handleCopy = (value) => {
        navigator.clipboard.writeText(value);
        onSnackbar('Copied to clipboard', 'success');
    };

    return (
        <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>

            {deactivation && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <b>{deactivation.disableLaunch || 'This project has been deactivated. Please contact sales@pagination.com'}</b>
                </Alert>
            )}

            {/* --- Sezione API Key --- */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <VpnKey color="primary" />
                    <Typography variant="h6">API Key</Typography>
                </Stack>

                {loadingKey ? (
                    <CircularProgress size={24} />
                ) : keyStatus?.exists ? (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Your API key for the external gateway <code>labels.pagination.com</code>.
                            Use it with <code>POST /v1/auth</code> to obtain a JWT.
                        </Typography>

                        {newKeyValue ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Save this key — it won't be shown again.
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <TextField
                                        size="small"
                                        value={showKey ? newKeyValue : maskApiKey(newKeyValue)}
                                        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                                        sx={{ flexGrow: 1 }}
                                    />
                                    <IconButton onClick={() => setShowKey(v => !v)} size="small">
                                        {showKey ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    <Tooltip title="Copy">
                                        <IconButton onClick={() => handleCopy(newKeyValue)} size="small">
                                            <ContentCopy />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Alert>
                        ) : (
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <TextField
                                    size="small"
                                    value={keyStatus?.masked_key || '••••••••••••••••••••••••'}
                                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                                    sx={{ flexGrow: 1 }}
                                />
                            </Stack>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <AlertDialog
                                title="Regenerate API Key"
                                text="The current key will be immediately invalidated for all Pagination services. Are you sure?"
                                handleActionOnAgree={handleRegenerateKey}
                                buttonProps={{ label: 'Regenerate', variant: 'outlined', size: 'small', startIcon: <Refresh /> }}
                            />
                            <AlertDialog
                                title="Remove API Key"
                                text="The key will be removed from the Labels service. It will continue to work for other Pagination services."
                                handleActionOnAgree={handleDeleteKey}
                                buttonProps={{ label: 'Remove', variant: 'outlined', size: 'small', color: 'error', startIcon: <Delete /> }}
                            />
                        </Stack>
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            No API key configured. Generate one to enable external integrations.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<VpnKey />}
                            onClick={handleCreateKey}
                            disabled={loadingKey}
                        >
                            Generate API Key
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* --- Sezione Info Integrazione --- */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <LinkIcon color="primary" />
                    <Typography variant="h6">External integration</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Base endpoint: <code>https://labels.pagination.com/v1/</code>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Flow: get a JWT with <code>POST /v1/auth</code> (header <code>x-api-key</code>),
                    then use <code>Authorization: Bearer &lt;jwt&gt;</code> for <code>POST /v1/render</code>.
                </Typography>
                <Box component="pre" sx={{
                    bgcolor: 'grey.100', p: 2, borderRadius: 1,
                    fontSize: '0.75rem', overflow: 'auto', mb: 2
                }}>
{`# 1. Get JWT (valid 1h)
curl -X POST https://labels.pagination.com/v1/auth \\
  -H "x-api-key: <your_api_key>"

# 2. Generate labels PDF
curl -X POST https://labels.pagination.com/v1/render \\
  -H "Authorization: Bearer <jwt>" \\
  -F "project_id=${selectedProject || 'customer/labels-project'}" \\
  -F "file=@products.csv" \\
  --output labels.pdf`}
                </Box>
                <Button
                    variant="text"
                    size="small"
                    href="https://pagination.com/api-label-v1/"
                    target="_blank"
                    rel="noopener"
                >
                    Full API documentation
                </Button>
            </Paper>

        </Box>
    );
}
